/**
 * Shared cache tag registry — single source of truth for tag metadata.
 *
 * Deploy-surviving fetch caches: add {@link CACHE_PERSISTENT_TAG} alongside the
 * domain tag (e.g. `["ashby-jobs", CACHE_PERSISTENT_TAG]`). Routed to the
 * `xiAssets` handler (`mw-nextjs:xi-assets:`) in frontend-next/src/cache-store-router.mjs
 * via the `xi-*` prefix rule.
 *
 * The `survivesBuilds ↔ cacheStore` relationship is enforced at compile time by the
 * {@link CacheTagMeta} discriminated union — TypeScript will reject any entry that mixes
 * `survivesBuilds: true` with `cacheStore: "redis-builds"` or vice-versa.
 *
 * revalidate values can be overridden via NEXT_PUBLIC_* env vars in frontend-next;
 * defaultRevalidate reflects the compile-time defaults.
 */

/**
 * Routing tag for deploy-surviving fetch caches that don't use an `xi-*` domain tag.
 * The `xi-` prefix causes the router to send the entry to `xiAssets` (`mw-nextjs:xi-assets:`).
 *
 * WARNING: Any future tag starting with `xi-` will also route to `xiAssets` (deploy-surviving).
 * If you're adding an ElevenLabs API tag, use an `xi-<name>` domain tag directly.
 * If you're adding any other deploy-surviving cache, use this constant as a second tag.
 * Do NOT invent new `xi-*` tags unless they represent ElevenLabs API data.
 */
export const CACHE_PERSISTENT_TAG = "xi-persistent";

type CacheTagMetaBase = {
  tag: string;
  label: string;
  description: string;
  /** Default TTL in seconds. May be overridden by NEXT_PUBLIC_* env vars in frontend-next. */
  defaultRevalidate: number;
};

/**
 * Discriminated union — `survivesBuilds` and `cacheStore` are always consistent:
 * - `survivesBuilds: true`  → `cacheStore: "redis-xi-assets"` (NOT version-keyed, persists across deploys)
 * - `survivesBuilds: false` → `cacheStore: "redis-builds"`    (version-keyed, cleared on each deploy)
 */
export type CacheTagMeta =
  | (CacheTagMetaBase & { survivesBuilds: true; cacheStore: "redis-xi-assets" })
  | (CacheTagMetaBase & { survivesBuilds: false; cacheStore: "redis-builds" });

/** Valid Redis handler names — derived from {@link CacheTagMeta}. */
export type CacheStore = CacheTagMeta["cacheStore"];

export const CACHE_TAG_REGISTRY_BASE = [
  {
    tag: "xi-assets-cache",
    label: "All XI Assets",
    description: "Broad invalidation — clears all XI API cached data (sounds + generations).",
    cacheStore: "redis-xi-assets",
    survivesBuilds: true,
    defaultRevalidate: 7 * 24 * 60 * 60, // 7 days (NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD)
  },
  {
    tag: "xi-shared-sounds",
    label: "XI Shared Sounds",
    description: "Shared sound generation results on SFX category pages.",
    cacheStore: "redis-xi-assets",
    survivesBuilds: true,
    defaultRevalidate: 7 * 24 * 60 * 60, // 7 days (NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD)
  },
  {
    tag: "xi-music",
    label: "XI Music",
    description: "Shared songs from the marketplace.",
    cacheStore: "redis-xi-assets",
    survivesBuilds: true,
    defaultRevalidate: 7 * 24 * 60 * 60, // 7 days (NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD)
  },
  {
    tag: "xi-shared-generations",
    label: "XI Shared Generations",
    description: "Shared image/video generation results on external model pages.",
    cacheStore: "redis-xi-assets",
    survivesBuilds: true,
    defaultRevalidate: 7 * 24 * 60 * 60, // 7 days (NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD)
  },
  {
    tag: "xi-iconic-voices",
    label: "XI Iconic Voices",
    description: "Iconic voice metadata from the ElevenLabs API (slider + table blocks).",
    cacheStore: "redis-xi-assets",
    survivesBuilds: true,
    defaultRevalidate: 7 * 24 * 60 * 60, // 7 days (NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD)
  },
  {
    tag: "ashby-jobs",
    label: "Ashby Job Listings",
    description: "Job postings and locations fetched from the Ashby API (/careers).",
    cacheStore: "redis-xi-assets",
    survivesBuilds: true,
    defaultRevalidate: 60 * 60, // 1 hour
  },
  {
    tag: "music-song",
    label: "Music Songs",
    description: "Shared music song data on music pages.",
    cacheStore: "redis-builds",
    survivesBuilds: false,
    defaultRevalidate: 5 * 60, // 5 min (NEXT_PUBLIC_CACHE_PERIOD)
  },
  {
    tag: "soundboard-sounds",
    label: "Soundboard Sounds",
    description: "CMS sound effects fetched for soundboard pages.",
    cacheStore: "redis-builds",
    survivesBuilds: false,
    defaultRevalidate: 5 * 60, // 5 min (NEXT_PUBLIC_CACHE_PERIOD)
  },
  {
    tag: "find-slug",
    label: "Find Slug",
    description: "Slug→collection lookup results used during page rendering.",
    cacheStore: "redis-builds",
    survivesBuilds: false,
    defaultRevalidate: 5 * 60, // 5 min (NEXT_PUBLIC_CACHE_PERIOD)
  },
  {
    tag: "posthog-feature-flags",
    label: "PostHog Feature Flags",
    description: "Server-side PostHog feature flag decisions.",
    cacheStore: "redis-builds",
    survivesBuilds: false,
    defaultRevalidate: 5 * 60, // 5 min (NEXT_PUBLIC_CACHE_PERIOD)
  },
  {
    tag: "product-ratings",
    label: "Product Ratings",
    description: "Apple App Store rating fetched from the iTunes Search API for SoftwareApp structured data.",
    cacheStore: "redis-xi-assets",
    survivesBuilds: true,
    defaultRevalidate: 24 * 60 * 60, // 24h
  },
  {
    tag: "markdown-cache",
    label: "Markdown variant cache",
    description: "Broad invalidation — clears all converted Markdown pages served via /next/markdown.",
    cacheStore: "redis-builds",
    survivesBuilds: false,
    defaultRevalidate: 5 * 60, // 5 min (NEXT_PUBLIC_PAYLOAD_CACHE_PERIOD)
  },
] as const satisfies CacheTagMeta[];

export type KnownCacheTag = (typeof CACHE_TAG_REGISTRY_BASE)[number]["tag"];

export const KNOWN_CACHE_TAGS = CACHE_TAG_REGISTRY_BASE.map(e => e.tag) as KnownCacheTag[];

// ─── Cache registry API types ────────────────────────────────────────────────
// Shared between frontend-next (cache-registry route) and cms-payload (CacheManager views).

export interface CacheRegistryGlobals {
  defaultCachePeriod: number;
  payloadCachePeriod: number;
  xiAssetsCachePeriod: number;
}

export interface AssetTranslationCacheMeta {
  label: string;
  description: string;
  ttlSeconds: number;
  keyPrefix: string;
}

export interface CacheRegistryResponse {
  tags: (CacheTagMeta & { revalidate: number })[];
  globals: CacheRegistryGlobals;
  assetTranslationCache: AssetTranslationCacheMeta;
}
